// Contact.js
import React, { useState } from 'react';
import {
  Mail, Globe, Linkedin, ArrowBigRight, CheckCircle, X
} from 'lucide-react';
import Button from './Button';

const Contact = () => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const contactInfo = [
    { icon: Mail, label: "Email Us", value: "hello@lusidea.com", link: "mailto:hello@lusidea.com" },
    { icon: Globe, label: "Our Office", value: "Ulica Franje Petračića 6, Zagreb, Croatia", link: "#" }
  ];

  // Spacing container constant
  const SPACING = {
    container: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-8',
    sectionMedium: 'py-12 md:py-16 lg:py-20'
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Extract form data
    const formData = new FormData(event.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      company: formData.get('company'),
      interest: formData.get('interest'),
      message: formData.get('message'),
      timestamp: new Date().toISOString()
    };

    try {
      // Send data to webhook
      await fetch('https://hook.eu2.make.com/fav4o8kusfjga07yzqdu9ld3npzxlp49', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      console.log("Form submitted and sent to webhook!");
      showToast("Thank you for your message! We'll get back to you soon.");
      event.target.reset();
    } catch (error) {
      console.error("Error sending form data:", error);
      // Still show success message to user even if webhook fails
      showToast("Thank you for your message! We'll get back to you soon.");
      event.target.reset();
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 lg:py-24 relative overflow-hidden">
      {/* Background with layered waves */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <svg className="absolute left-0 top-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#f9fafb" fillOpacity="0.5" d="M0,32L48,69.3C96,107,192,181,288,197.3C384,213,480,171,576,149.3C672,128,768,128,864,144C960,160,1056,192,1152,181.3C1248,171,1344,117,1392,90.7L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
        </svg>
        
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: 'radial-gradient(#e5e7eb 1px, transparent 1px)', backgroundSize: '1rem 1rem'}} aria-hidden="true"></div>
      </div>
      
      <div className={SPACING.container}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left Column: Info */}
          <div className="lg:col-span-5">
            <div className="max-w-md">
              <div className="inline-block rounded-full bg-primaryLight px-4 py-1 mb-4">
                <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
                  Let's Connect
                </h2>
              </div>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-5">
                Ready to Start Your <span className="text-primary">Innovation</span> Journey?
              </p>
              <p className="text-base sm:text-lg text-darkGray mb-8 md:mb-10">
                Reach out to discuss your specific challenges and goals. Our team is ready to help you transform your ideas into impact.
              </p>

              {/* Contact details list with animated icons */}
              <div className="space-y-6 mb-8 md:mb-10">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-12 h-12 rounded-xl bg-primaryLight flex-shrink-0 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      {React.createElement(item.icon, { size: 20 })}
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-semibold text-mediumGray">{item.label}</div>
                      <a href={item.link} className="text-base sm:text-lg text-black hover:text-primary transition-colors break-words">
                        {item.value}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social links */}
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/lusidea/" aria-label="Lusidea on LinkedIn" className="w-12 h-12 rounded-full bg-lightGray text-mediumGray flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Form with floating effect */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 md:p-10 border border-lightGray relative transform hover:-translate-y-2 transition-all duration-300">
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-secondary rounded-lg transform rotate-12 hidden sm:block"></div>
              <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-primary rounded-lg transform -rotate-12 hidden sm:block"></div>
              
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-6">Send us a message</h3>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-darkGray mb-2">Name <span className="text-primary">*</span></label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      className="block w-full px-4 py-3 text-base text-darkGray bg-white border border-lightGray rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder-mediumGray" 
                      placeholder="Your Full Name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-darkGray mb-2">Work Email <span className="text-primary">*</span></label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      className="block w-full px-4 py-3 text-base text-darkGray bg-white border border-lightGray rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder-mediumGray" 
                      placeholder="you@company.com" 
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-darkGray mb-2">Company <span className="text-primary">*</span></label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company" 
                    required 
                    className="block w-full px-4 py-3 text-base text-darkGray bg-white border border-lightGray rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder-mediumGray" 
                    placeholder="Your Company Name" 
                  />
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-darkGray mb-2">Primary Interest <span className="text-primary">*</span></label>
                  <select 
                    id="interest" 
                    name="interest" 
                    required 
                    className="block w-full px-4 py-3 text-base text-darkGray bg-white border border-lightGray rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder-mediumGray appearance-none bg-no-repeat bg-[right_0.75rem_center] bg-[length:1.25em_1.25em] pr-10" 
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>Select an interest...</option>
                    <option value="Innovation Management">Innovation Management</option>
                    <option value="Future Thinking">Future Thinking</option>
                    <option value="Business Design">Business Design</option>
                    <option value="Self-Service Innovation">Self-Service Innovation / Tools</option>
                    <option value="Partnership">Partnership Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-darkGray mb-2">Message <span className="text-primary">*</span></label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="4" 
                    required 
                    className="block w-full px-4 py-3 text-base text-darkGray bg-white border border-lightGray rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder-mediumGray min-h-[120px] sm:min-h-[140px]" 
                    placeholder="Tell us briefly about your needs or questions..."
                  ></textarea>
                </div>

                <div>
                  <Button type="submit" variant="primary" size="lg" fullWidth icon={ArrowBigRight}>
                    Send Your Inquiry
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className="bg-white border border-lightGray rounded-lg shadow-lg p-4 pr-12 max-w-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-darkGray">{toast.message}</p>
              </div>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="absolute top-3 right-3 text-mediumGray hover:text-darkGray transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Contact;